import { useState } from 'react';
import {
  DEFAULT_SELECTION,
  EXPORT_SECTIONS,
  loadPresets,
  savePreset,
  deletePreset,
} from '../model/exportColumns';
import { exportPacientesExcelDynamic } from '../api/pacientesApi';

const currentYear = new Date().getFullYear();

export const useExportBuilder = () => {
  const [mode, setMode] = useState('comparison');
  const [year, setYear] = useState(currentYear - 1);
  const [selection, setSelection] = useState(DEFAULT_SELECTION);
  const [presets, setPresets] = useState(loadPresets);
  const [presetName, setPresetName] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const toggleField = (sectionId, fieldId) => {
    setSelection((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [fieldId]: !prev[sectionId][fieldId],
      },
    }));
  };

  const toggleSection = (sectionId) => {
    const section = EXPORT_SECTIONS.find((s) => s.id === sectionId);
    if (!section) return;
    const allChecked = section.fields.every((f) => selection[sectionId]?.[f.id]);
    setSelection((prev) => ({
      ...prev,
      [sectionId]: Object.fromEntries(section.fields.map((f) => [f.id, !allChecked])),
    }));
  };

  const isSectionChecked = (sectionId) => {
    const section = EXPORT_SECTIONS.find((s) => s.id === sectionId);
    return section?.fields.every((f) => selection[sectionId]?.[f.id]) ?? false;
  };

  const isSectionIndeterminate = (sectionId) => {
    const section = EXPORT_SECTIONS.find((s) => s.id === sectionId);
    if (!section) return false;
    const checked = section.fields.filter((f) => selection[sectionId]?.[f.id]).length;
    return checked > 0 && checked < section.fields.length;
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    const updated = savePreset(presetName.trim(), { mode, year, selection });
    setPresets(updated);
    setPresetName('');
  };

  const handleLoadPreset = (preset) => {
    setMode(preset.config.mode);
    setYear(preset.config.year);
    setSelection(preset.config.selection);
  };

  const handleDeletePreset = (name) => {
    setPresets(deletePreset(name));
  };

  const buildConfig = () => ({ mode, year: parseInt(year, 10), sections: selection });

  const handleDownload = async () => {
    const hasAnyField = EXPORT_SECTIONS.some((section) =>
      section.fields.some((f) => selection[section.id]?.[f.id])
    );
    if (!hasAnyField) {
      setError('Selecciona al menos una columna para exportar.');
      return;
    }

    try {
      setError('');
      setDownloading(true);
      const response = await exportPacientesExcelDynamic(buildConfig());
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const match = disposition && /filename="?([^";]+)"?/i.exec(disposition);
      link.setAttribute('download', match ? match[1] : `pacientes-${mode}-${year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess(true);
    } catch (err) {
      console.error('Error downloading Excel:', err);
      setError('No se pudo descargar el archivo. Intente nuevamente.');
    } finally {
      setDownloading(false);
    }
  };

  return {
    mode, setMode,
    year, setYear,
    selection,
    presets,
    presetName, setPresetName,
    downloading,
    error, setError,
    success, setSuccess,
    toggleField,
    toggleSection,
    isSectionChecked,
    isSectionIndeterminate,
    handleSavePreset,
    handleLoadPreset,
    handleDeletePreset,
    handleDownload,
  };
};
